import {Vector, SierraCanvas, ColorGradient} from '@teemill/common/classes';
import {hsvToHex, rgbToHex} from '@teemill/utilities';
import {Mathf} from './omnicanvas.utilities';

export default class OmniCanvasRenderer {
  engine;

  queuedForRender = false;
  outputSuppressed = false;

  includeFilters = true;
  includeBounds = true;
  antiAliasing = true;

  ghostLayerCache = {};

  queuedAnimationFrameId;

  constructor(engine) {
    this.engine = engine;
    this._createCanvasElements();

    this.renderImmediately = this.renderImmediately.bind(this);
  }

  /**
   * ### Getters
   */

  get engineParams() {
    return this.engine?.params;
  }

  get boundingBox() {
    return this.engine.boundingBox;
  }

  /**
   * This is the difference between a CSS pixel and a canvas pixel. e.g. if the
   * canvas is 1000 pixels wide, but is being displayed at 500, then the pixel scale
   * is 2 because there are 2 canvas pixels to every actual pixel in each axis.
   */
  get pixelScale() {
    return 1 / (this.canvasElement.scrollWidth / this.virtualCanvas.width);
  }

  get width() {
    return this.virtualCanvas.width;
  }

  get height() {
    return this.virtualCanvas.height;
  }

  get shouldShowBounds() {
    const activeEl = this.engine.activeEl;

    if (!this.boundingBox) {
      return false;
    }

    if (typeof this.boundingBox.options.show === 'function') {
      return this.boundingBox.options.show();
    }

    return (
      this.boundingBox.options.force ||
      (this.boundingBox.options.show &&
        (this.engine.mouseState === 'down' ||
          (activeEl &&
            ((activeEl.object && activeEl.object.isOut()) ||
              (!activeEl.object && activeEl.isOut())))))
    );
  }

  get shouldShowGuidelines() {
    return this.engine.activeEl && this.engine.mouseState === 'down';
  }

  get bounds() {
    return this.boundingBox;
  }

  /**
   * returns a hex colour based on the luminance value of the current background, to
   * make sure that the bounds always contrast against the background
   *
   * The hex will always be grayscale
   *
   * @return {string} a hex value
   */
  get boundsHex() {
    if (this.engineParams.bounds.color) {
      return this.engineParams.bounds.color;
    }

    const rotatedLum = (1 + (this.engineParams.baseLuminance - 0.5)) % 1;
    return hsvToHex({h: 0, s: 0, v: rotatedLum});
  }

  get boundsLineDash() {
    return this.engineParams.bounds.lineDash;
  }

  get extraBoundsLineDash() {
    const activeEl = this.engine.activeEl;

    if (!this.boundingBox.options.extra.contains(activeEl)) {
      return [5, 10];
    }

    return [];
  }

  _createCanvasElements() {
    // The output canvas is the canvas the user sees an interacts with
    this.outputCanvas = SierraCanvas.fromCanvas(this.engine.canvasElement);
    this.canvasElement = this.outputCanvas.canvas;
    this.outputCanvas.resizeCanvas(
      this.engineParams.stage.width,
      this.engineParams.stage.height
    );

    this.engine.canvasElement.classList.add('omni-canvas');

    // The virtual canvas is an offscreen canvas
    // Objects are rendered to this canvas, which is then rendered to the output canvas
    // at the end of each frame. This is better for performance than directly rendering
    // to the output canvas
    this.virtualCanvas = this._createCanvas(
      this.engineParams.stage.width,
      this.engineParams.stage.height
    );

    // The cache canvas is a tertiary canvas that is used to periodically take
    // snapshot caches of the virtual canvas for when we want to modify the virtual
    // canvas while retaining some data
    this.cacheCanvas = this._createCanvas(
      this.engineParams.stage.width,
      this.engineParams.stage.height
    );
  }

  _createCanvas(width, height) {
    return new SierraCanvas(width, height);
  }

  /**
   * Handles the render call from the parent canvas. This either renders a frame or queues
   * a frame to be rendered at the next available opportunity (preferred)
   *
   * @param {Boolean} includeFilters whether or not to process filters
   * @param {Boolean} includeBounds whether or not to process bound filters (fade out)
   * @param {Boolean} withAntiAliasingEnabled if false, disable the browser's native
   * canvas antialiasing
   * @param {Boolean} async if true, the render will be performed asynchronously before
   * the browser's next render - this is better for performance but has the caveat that
   * the canvas won't be updated immediately. Generally this can be left as true unless
   * you need to create an image from the result (e.g. when exporting)
   *
   * @return {void}
   */
  render({
    includeFilters = true,
    includeBounds = true,
    includeStaticLayerImages = true,
    withAntiAliasing = true,
    async = true,
  } = {}) {
    this.includeFilters = includeFilters;
    this.includeBounds = includeBounds;
    this.antiAliasing = withAntiAliasing;
    this.includeStaticLayerImages = includeStaticLayerImages;

    this.cancelQueuedRender();

    if (async) {
      this.queueRender();
    } else {
      this.renderImmediately();
    }
  }

  cancelQueuedRender() {
    if (this.queuedAnimationFrameId) {
      cancelAnimationFrame(this.queuedAnimationFrameId);
      this.queuedAnimationFrameId = undefined;
    }
  }

  queueRender() {
    this.queuedAnimationFrameId = requestAnimationFrame(this.renderImmediately);
  }

  renderImmediately() {
    if (!this.engine) {
      return;
    }

    if (!this.engineParams.rendering || this.engineParams.exportMode) {
      this.engineParams.rendering = true;
      this.virtualCanvas.clear().save();

      this.translateToFocalPoint();

      this.virtualCanvas.save();

      /**
       * By default the browser applies anti-aliasing to the canvas which can create
       * a blurred effect to the edge of the image. This makes the image look better
       * in the browser but worse when printed
       *
       * For this reason, antiAliasingEnabled should be disabled when exporting
       * the final image (if the final image is intended for printing)
       */
      this.virtualCanvas.imageSmoothingEnabled = this.antiAliasing;

      this.beforeRender();

      if (this.includeFilters) {
        this.applyFilters();
      }

      this.renderObjects(this.includeFilters);

      this.virtualCanvas.restore();

      if (this.includeBounds) {
        this.drawBounds();
      }

      if (this.includeStaticLayerImages) {
        this.drawStaticLayerImages();
        this.drawGhostLayers();
      }

      this.virtualCanvas.restore();

      if (this.engineParams.background.fill) {
        this.drawBackgroundFill();
      }

      this.afterRender();

      this.engineParams.rendering = false;
    }
  }

  translateToFocalPoint() {
    if (this.engineParams.stage.zoomToBounds) {
      const canvasScale = this.engineParams.scale;
      const distanceToMidPoint =
        this.getDeltaFromBoundsMidPointToCanvasMidPoint().scale(
          canvasScale - 1
        );

      this.virtualCanvas.translate(
        -distanceToMidPoint.x,
        -distanceToMidPoint.y
      );
    }
  }

  getDeltaFromBoundsMidPointToCanvasMidPoint() {
    const globalBounds = this.engine.boundingBox;
    const focalPoint = this.engineParams?.stage?.focalPoint;

    if (!globalBounds) {
      return new Vector(0, 0);
    }

    const canvasMidPoint = new Vector(
      this.virtualCanvas.width / 2,
      this.virtualCanvas.height / 2
    );
    const boundsMidPoint = focalPoint
      ? new Vector(focalPoint.x, focalPoint.y)
      : new Vector(
          globalBounds.position.x + globalBounds.width / 2,
          globalBounds.position.y + globalBounds.height / 2
        );

    return new Vector(
      Math.floor(boundsMidPoint.x - canvasMidPoint.x),
      Math.floor(boundsMidPoint.y - canvasMidPoint.y)
    );
  }

  /**
   * renders all objects on the current active layer
   *
   * @return {void}
   */
  renderObjects() {
    const objects = this.engine
      .getObjects(this.engine.activeLayerName)
      .filter(obj => obj.ctx);

    objects.forEach(object => {
      if (object.enabled) {
        object.render(this.includeFilters);
      }
    });
  }

  /**
   * This code is executed at the beginning of each frame, before objects are rendered
   *
   * @return {void}
   */
  beforeRender() {
    this.clear();

    this.clampCanvasScale();

    this.translateCanvasToBounds();

    this.resetGuidelines();
  }

  /**
   * Clamps canvas scale between the min and max zoom values
   *
   * @return {void}
   */
  clampCanvasScale() {
    this.engineParams.scale = Mathf.clamp(
      this.engineParams.scale,
      this.engineParams.stage.minZoom,
      this.engineParams.stage.maxZoom
    );
  }

  /**
   * Applies translation to the canvas so that the coordinate (0, 0) is the top left
   * corner of the bounding box. This means that all further coordinates are relative
   * to the bounds
   *
   * @return {void}
   */
  translateCanvasToBounds() {
    if (this.boundingBox && this.engineParams.scale !== 1) {
      const bounds = this.boundingBox;
      const canvasScale = this.engineParams.scale;
      const bp = bounds.position;

      // translate the canvas so that (0,0) is the top left corner of the bounds area
      this.virtualCanvas.translate(
        -bp.x * (canvasScale - 1) +
          (bp.x - bounds.width / 2) * (canvasScale - 1),
        -bp.y * (canvasScale - 1) +
          (bp.y - bounds.height / 2) * (canvasScale - 1)
      );
    }
  }

  /**
   * Mark all guidelines as being inactive, preventing them from being highlighted
   * in the canvas
   *
   * @return {void}
   */
  resetGuidelines() {
    this.engineParams.guidelines.forEach(guideline => {
      guideline.hide();
    });
  }

  /**
   * This occurs after a frame is rendered
   *
   * @return {void}
   */
  afterRender() {
    if (!this.engineParams.exportMode && !this.outputSuppressed) {
      this.loadVirtualCanvasToDisplayCanvas();
    }

    this.engine.canvasElement.dispatchEvent(new CustomEvent('rendered'));

    this.queuedForRender = false;

    this.runRenderCallbacks();
  }

  /**
   * Take the virtual (off screen) canvas and load it into the display (on screen)
   * canvas
   *
   * @return {void}
   */
  loadVirtualCanvasToDisplayCanvas() {
    this.outputCanvas.clearRect(
      0,
      0,
      this.outputCanvas.width,
      this.outputCanvas.height
    );
    this.outputCanvas.drawImage(this.virtualCanvas.canvas, 0, 0);
  }

  /**
   * When output is suppressed, changes made to the virtual canvas will not be copied over to the output canvas.
   * the canvas that the user sees will remain static until output is unsuppressed, resulting in improved
   * rendering performance and a better visual experience
   */
  suppressOutput() {
    this.outputSuppressed = true;
  }

  unsuppressOutput() {
    this.outputSuppressed = false;
  }

  /**
   * Exports the active layer to either a base 64 string or imagedata
   *
   * @param {String} format The format to export to
   * @default image/png
   * One of image/png, image/jpeg, image/webp, image/svg, imagedata
   *
   * image/webp creates smaller file sizes with no drop in quality vs image/png
   * but is only supported by Chrome as of August 2018
   * however, other browsers will fallback to image/png so it is perfectly safe
   * to pass through 'image/webp' in all browsers
   *
   * @param {Float} quality The quality of the created image on a scale of 0 to 1
   * @default 0.92
   * This only affects image/jpeg and image/webp formats
   *
   * @param {Float} scale The scale of the created image, where 1 is the size of the canvas
   * @param {Float} zoom The level of zoom of the canvas
   * @default 1
   * @param {Boolean} antiAliasingEnabled if true, disable the browser's native
   * anti-aliasing
   *
   * @return {String} a Base64 representation of the Canvas
   * @return {ImageData} when format == 'imagedata'
   */
  export(
    format,
    quality,
    scale = 1,
    zoom = 1,
    antiAliasingEnabled = true,
    blobCallback
  ) {
    let result;

    this.engineParams.exportMode = true;

    if (this.engine.hasValidBounds()) {
      result = this.exportFromBounds(
        format,
        quality,
        scale,
        zoom,
        antiAliasingEnabled,
        blobCallback
      );
    } else {
      this.render({
        includeBounds: false,
        includeStaticLayerImages: false,
        async: false,
      });
      result = this.exportFromCanvas(format, quality, blobCallback);
    }

    this.render();

    return result;
  }

  exportAsBlob(
    callback,
    format,
    quality,
    scale = 1,
    zoom = 1,
    antiAliasingEnabled = true
  ) {
    this.export(format, quality, scale, zoom, antiAliasingEnabled, callback);
  }

  /**
   * Stores various settings in the canvas before export. These settings will change
   * during the export process and must be restored to their original values after
   * the export is complete
   *
   * @return {Object}
   */
  stashExportParams() {
    this._stashedExportParams = {
      boundingBox: new Vector(
        this.boundingBox.position.x,
        this.boundingBox.position.y
      ),
      canvas: new Vector(this.virtualCanvas.width, this.virtualCanvas.height),
      scale: this.engineParams.scale,
      globalTranslation: new Vector(
        this.engineParams.globalTranslation?.x ?? 0,
        this.engineParams.globalTranslation?.y ?? 0
      ),
      maxZoom: this.engineParams.stage.maxZoom,
      focalPoint: this.engineParams.stage.focalPoint
        ? new Vector(
            this.engineParams.stage.focalPoint.x,
            this.engineParams.stage.focalPoint.y
          )
        : null,
      clearFocalPointOnZoomReset:
        this.engineParams.stage.clearFocalPointOnZoomReset ?? false,
      image: {
        mipMapsEnabled: this.engineParams.image.mipMapsEnabled,
      },
      snapping: {
        position: this.engineParams.snapping.position,
      },
    };

    return this._stashedExportParams;
  }

  /**
   * Takes a set of options and reapplies them to the canvas after a successful
   * export
   *
   * @param {Object} stashedParams the parameters to restore
   *
   * @return {void}
   */
  restoreExportParams() {
    const stashedParams = this._stashedExportParams;

    this.virtualCanvas.resizeCanvas(
      this.outputCanvas.width,
      this.outputCanvas.height
    );

    this.engineParams.scale = stashedParams.scale;
    this.engineParams.globalTranslation = new Vector(
      stashedParams.globalTranslation.x,
      stashedParams.globalTranslation.y
    );
    this.engineParams.image.mipMapsEnabled = stashedParams.image.mipMapsEnabled;
    this.engineParams.stage.maxZoom = stashedParams.maxZoom;
    this.engineParams.stage.focalPoint = stashedParams.focalPoint
      ? new Vector(stashedParams.focalPoint.x, stashedParams.focalPoint.y)
      : null;
    this.engineParams.stage.clearFocalPointOnZoomReset =
      stashedParams.clearFocalPointOnZoomReset;
    this.engineParams.snapping.position = stashedParams.snapping.position;

    this.engineParams.exportMode = false;
  }

  prepareExport(scale = 1, zoom = 1, antiAliasingEnabled = true) {
    this.engineParams.snapping.position = 0;

    this.engineParams.stage.maxZoom = Math.Infinity;
    this.engineParams.stage.focalPoint = null;
    this.engineParams.stage.clearFocalPointOnZoomReset = false;
    this.engineParams.globalTranslation = new Vector(0, 0);
    this.engineParams.scale = scale * zoom;

    this.boundingBox.position.x = this.boundingBox.width * ((scale - 1) / 2);
    this.boundingBox.position.y = this.boundingBox.height * ((scale - 1) / 2);

    this.engineParams.mipMapsEnabled = false;

    this.virtualCanvas.resizeCanvas(
      Math.round(this.boundingBox.width * scale),
      Math.round(this.boundingBox.height * scale)
    );

    this.render({
      includeBounds: false,
      includeStaticLayerImages: false,
      withAntiAliasing: antiAliasingEnabled,
      async: false,
    });

    if (this.boundingBox.cutout) {
      const canvasScale = this.engineParams.scale;

      if (!(this.boundingBox.cutout instanceof Image)) {
        this.virtualCanvas
          .setCompositeOperation('destination-in')
          .beginPath()
          .fill(this.boundingBox.cutout(this.boundingBox.width * canvasScale))
          .closePath();
      }
    }
  }

  exportFromCanvas(format, quality, blobCallback) {
    if (blobCallback) {
      return this.virtualCanvas.toBlob(
        blob => {
          blobCallback.apply(null, [blob]);
          this.restoreExportParams();
        },
        format,
        quality
      );
    }

    if (format === true || format === false || !format) {
      return this.virtualCanvas.toDataURL(
        format ? 'image/png' : 'image/jpeg',
        quality || 0.92
      );
    }

    if (format === 'imageData') {
      return this.virtualCanvas.getImageData(
        0,
        0,
        this.virtualCanvas.width,
        this.virtualCanvas.height
      );
    }

    return this.virtualCanvas.toDataURL(format, quality || 0.92);
  }

  exportFromBounds(
    format,
    quality,
    scale,
    zoom,
    antiAliasingEnabled,
    blobCallback
  ) {
    const stashedParams = this.stashExportParams();

    this.prepareExport(scale, zoom, antiAliasingEnabled);

    this.boundingBox.position.x = stashedParams.boundingBox.x;
    this.boundingBox.position.y = stashedParams.boundingBox.y;

    const result = this.exportFromCanvas(format, quality, blobCallback);

    if (!blobCallback) {
      this.restoreExportParams();
    }

    return result;
  }

  /**
   * Uses various techniques to highlight the design area, or fade the out of bounds area
   *
   * @param bounds The area within these bounds will be highlighted
   *
   * The method used to highlight/fade depends on this.engineParams.stage.fadeMethod
   * @return {void}
   */
  highlightDesignArea(bounds) {
    this.cacheDesignArea();

    if (this.engineParams.stage.fadeMethod === 'block') {
      this.applyBlockFadeMethod(bounds);
    } else if (this.engineParams.stage.fadeMethod === 'alpha') {
      this.applyAlphaFadeMethod();
    }

    this.loadCachedDesignArea(bounds);
  }

  cacheDesignArea() {
    const cacheCanvas = this.cacheCanvas;
    const canvasScale = this.engineParams.scale;

    if (this.boundingBox.cutout) {
      if (this.boundingBox.cutout instanceof Image) {
        this.cacheCanvas
          .resizeCanvas(this.virtualCanvas.width, this.virtualCanvas.height)
          .drawImage(this.virtualCanvas.canvas, 0, 0)
          .setCompositeOperation('destination-in')
          .drawImage(
            this.boundingBox.cutout,
            (this.virtualCanvas.width / 2) * -(canvasScale - 1),
            (this.virtualCanvas.height / 2) * -(canvasScale - 1),
            this.virtualCanvas.width * canvasScale,
            this.virtualCanvas.width *
              (this.boundingBox.cutout.height / this.boundingBox.cutout.width) *
              canvasScale
          );
      } else {
        const originPoint = this.engine.canvasCenterPoint;

        cacheCanvas
          .resizeCanvas(this.virtualCanvas.width, this.virtualCanvas.height)
          .drawImage(this.virtualCanvas.canvas, 0, 0)
          .setCompositeOperation('destination-in')
          .beginPath()
          .translate(
            originPoint.x + this.boundingBox.position.x * canvasScale,
            originPoint.y + this.boundingBox.position.y * canvasScale
          )
          .fill(this.boundingBox.cutout(this.boundingBox.width * canvasScale))
          .closePath();
      }
    } else {
      cacheCanvas
        .clear()
        .resizeCanvas(this.virtualCanvas.width, this.virtualCanvas.height)
        .drawImage(this.virtualCanvas.canvas, 0, 0);
    }
  }

  /**
   * reverts the canvas to a blank slate, necessary before a render
   * @return {void}
   */
  clear() {
    this.virtualCanvas.clear();
    this.cacheCanvas.clear();
  }

  /**
   * display bounding box
   * @return {void}
   */
  drawBounds() {
    this.virtualCanvas.save();
    const activeEl = this.engine.activeEl;
    const canvasScale = this.engineParams.scale;
    const bounds = this.boundingBox;
    const originPoint = this.engine.canvasCenterPoint;

    if (this.shouldShowBounds) {
      this._renderBoundingBox(canvasScale, originPoint);
    }

    if (this.boundingBox && this.engineParams.stage.fadeOutOfBounds) {
      this.highlightDesignArea(bounds, canvasScale);
    }

    this.virtualCanvas.restore().save();

    if (this.shouldShowGuidelines) {
      this.drawGuidelines(originPoint);
    }

    if (activeEl && activeEl.controllable && activeEl.controller.activeEl) {
      this.drawActiveElBounds(activeEl);
    }

    this.virtualCanvas.restore();
  }

  _renderBoundingBox(canvasScale, originPoint) {
    this.virtualCanvas.strokeStyle = this.boundsHex;

    this.virtualCanvas.save();

    if (this.engineParams.bounds.dashed) {
      this.virtualCanvas.setLineDash(this.boundsLineDash);
    }

    // draw custom bounds
    if (
      this.boundingBox.cutout &&
      !(this.boundingBox.cutout instanceof Image)
    ) {
      this.virtualCanvas
        .beginPath()
        .translate(
          originPoint.x + this.boundingBox.position.x * canvasScale,
          originPoint.y + this.boundingBox.position.y * canvasScale
        )
        .stroke(this.boundingBox.cutout(this.boundingBox.width * canvasScale))
        .closePath();
    } else {
      if (this.boundingBox.cutout instanceof Image) {
        const dArr = [-1, -1, 0, -1, 1, -1, -1, 0, 1, 0, -1, 1, 0, 1, 1, 1];

        for (let i = 0; i < dArr.length; i += 2) {
          const cnv = SierraCanvas.global;

          cnv.resizeCanvas(this.virtualCanvas.width, this.virtualCanvas.height);
          cnv
            .translate(
              (this.virtualCanvas.width / 2) * -(canvasScale - 1),
              (this.virtualCanvas.height / 2) * -(canvasScale - 1)
            )
            .drawImage(
              this.boundingBox.cutout,
              dArr[i],
              dArr[i + 1],
              this.virtualCanvas.width * canvasScale,
              this.virtualCanvas.width *
                (this.boundingBox.cutout.height /
                  this.boundingBox.cutout.width) *
                canvasScale
            )
            .setCompositeOperation('source-in')
            .setFillStyle(this.boundsHex)
            .fillRect(
              0,
              0,
              this.virtualCanvas.width * canvasScale,
              this.virtualCanvas.width *
                (this.boundingBox.cutout.height /
                  this.boundingBox.cutout.width) *
                canvasScale
            )
            .setCompositeOperation('destination-out')
            .drawImage(
              this.boundingBox.cutout,
              0,
              0,
              this.virtualCanvas.width * canvasScale,
              this.virtualCanvas.width *
                (this.boundingBox.cutout.height /
                  this.boundingBox.cutout.width) *
                canvasScale
            );

          this.virtualCanvas.drawImage(
            cnv,
            this.boundingBox.position.x * canvasScale,
            this.boundingBox.position.y * canvasScale,
            this.boundingBox.width * canvasScale,
            this.boundingBox.height * canvasScale,
            this.boundingBox.position.x * canvasScale,
            this.boundingBox.position.y * canvasScale,
            this.boundingBox.width * canvasScale,
            this.boundingBox.height * canvasScale
          );
        }
      }
      this.virtualCanvas
        .beginPath()
        .strokeRect(
          originPoint.x + this.boundingBox.position.x * canvasScale,
          originPoint.y + this.boundingBox.position.y * canvasScale,
          this.boundingBox.width * canvasScale,
          this.boundingBox.height * canvasScale
        )
        .closePath();
    }

    if (this.boundingBox.options.extra) {
      this.drawExtraBounds(originPoint, canvasScale);
    }
    this.virtualCanvas.restore();
  }

  /**
   * Draw active guidelines (lines that are currently being snapped to)
   *
   * @return {void}
   */
  drawGuidelines() {
    this.virtualCanvas.save().setCompositeOperation('difference');
    this.virtualCanvas.strokeStyle = '#1565c0';

    this.engineParams.guidelines.forEach(guideline => {
      if (guideline.visible || this.engineParams.guideline.mode === 'static') {
        guideline.render();
      }
    });

    this.virtualCanvas.restore();
  }

  /**
   * Extra bounds are an additional set of bounds that recommend where to place the
   * design. Designs may exist outside of these bounds without penalty
   *
   * @param {Vector} originPoint The top left corner of the extra bounds
   * @param {Number} canvasScale The scale of the canvas
   */
  drawExtraBounds(originPoint, canvasScale) {
    const pbp = this.boundingBox.position;
    const pebp = this.boundingBox.options.extra.position;

    this.virtualCanvas
      .save()
      .beginPath()
      .setLineDash(this.boundingBox.options.lineDash)
      .strokeRect(
        originPoint.x + (pbp.x + pebp.x) * canvasScale,
        originPoint.y + (pbp.y + pebp.y) * canvasScale,
        this.boundingBox.options.extra.width * canvasScale,
        this.boundingBox.options.extra.height * canvasScale
      )
      .closePath();

    this.virtualCanvas.font = '16px Lato, sans-serif';

    const textWidth = this.virtualCanvas.measureText(
      this.boundingBox.options.extra.label
    ).width;

    if (this.boundingBox.options.extra.label) {
      this.virtualCanvas
        .fillText(
          this.boundingBox.options.extra.label,
          originPoint.x +
            (pbp.x + pebp.x) * canvasScale +
            ((this.boundingBox.options.extra.width / 2) * canvasScale -
              textWidth / 2),
          originPoint.y + ((pbp.y + pebp.y) * canvasScale - 4)
        )
        .restore();
    }
  }

  loadCachedDesignArea(bounds) {
    const cacheCanvas = this.cacheCanvas;
    const scale = bounds.fixed ? 1 : this.engineParams.scale;
    const globalTranslation = bounds.fixed
      ? {x: 0, y: 0}
      : this.engineParams?.globalTranslation ?? {x: 0, y: 0};

    const x = Math.round(
      bounds.position.x +
        globalTranslation.x -
        (bounds.width / 2) * (scale - 1) -
        1
    );
    const y = Math.round(
      bounds.position.y +
        globalTranslation.y -
        (bounds.height / 2) * (scale - 1) -
        1
    );
    const w = Math.round(bounds.width * scale + 2);
    const h = Math.round(bounds.height * scale + 2);

    const distanceToMidPoint = bounds.fixed
      ? new Vector(0, 0)
      : this.getDeltaFromBoundsMidPointToCanvasMidPoint().scale(
          scale - 1
        );

    // sourceX and sourceY determine where to crop the source image from
    const sourceX = x - distanceToMidPoint.x;
    const sourceY = y - distanceToMidPoint.y;

    let sx = sourceX;
    let sy = sourceY;
    let dx = x;
    let dy = y;
    let sw = w;
    let sh = h;

    if (sx < 0) {
      dx += -sx;
      sw += sx;
      sx = 0;
    }

    if (sy < 0) {
      dy += -sy;
      sh += sy;
      sy = 0;
    }

    if (sx + sw > cacheCanvas.width) {
      sw = cacheCanvas.width - sx;
    }

    if (sy + sh > cacheCanvas.height) {
      sh = cacheCanvas.height - sy;
    }

    if (sw <= 0 || sh <= 0) {
      return;
    }

    this.virtualCanvas
      .setCompositeOperation('source-over')
      // .clearRect(x, y, w, h)
      .drawImage(cacheCanvas.canvas, sx, sy, sw, sh, dx, dy, sw, sh);
  }

  applyBlockFadeMethod(bounds) {
    this.virtualCanvas.setCompositeOperation('source-in').save();

    const alpha = this.engineParams.stage.fadeAlpha;
    const globalTranslation = this.engineParams?.globalTranslation ?? {
      x: 0,
      y: 0,
    };

    if (!bounds.fixed) {
      this.virtualCanvas.scaleFromPosition(
        this.engineParams.scale,
        this.engineParams.scale,
        bounds.position.x + bounds.width / 2,
        bounds.position.y + bounds.height / 2
      );
    }

    this.virtualCanvas.fillStyle = `rgba(126, 126, 126, ${alpha})`;

    this.engine.getObjects().forEach(object => {
      if (!object.outerBounds) {
        object.outerBounds = object.calculateOuterBounds();
      }

      // if the engine has custom bounds then it's very complicated to determine
      // if a design is out of bounds, so for simplicity, we can just assume it is.
      // this has a small performance overhead if lots of designs are within the bounds
      const hasCustomBounds = this.boundingBox?.cutout;

      if (hasCustomBounds || object.isOut()) {
        this.virtualCanvas.rect(
          bounds.position.x +
            globalTranslation.x +
            object.position.x +
            object.outerBounds.tl.x,
          bounds.position.y +
            globalTranslation.y +
            object.position.y +
            object.outerBounds.tl.y,
          object.outerBounds.br.x - object.outerBounds.tl.x,
          object.outerBounds.br.y - object.outerBounds.tl.y
        );
      }
    });

    this.virtualCanvas.fill().restore();
  }

  applyAlphaFadeMethod() {
    const cacheCanvas = this.cacheCanvas;
    const width = this.virtualCanvas.width;
    const height = this.virtualCanvas.height;

    this.virtualCanvas.clear();

    this.virtualCanvas
      .save()
      .setGlobalAlpha(0.5)
      .drawImage(cacheCanvas.canvas, 0, 0)
      .setGlobalAlpha(1)
      .setCompositeOperation('saturation')
      .setFillStyle('rgba(255, 255, 255, 1)')
      .fillRect(0, 0, width, height)
      .setCompositeOperation('destination-in')
      .drawImage(cacheCanvas.canvas, 0, 0)
      .restore();
  }

  /**
   * Draws the bounds square and controls for the an element
   *
   * @param {OmniObject} activeEl The element to draw bounds for
   * @return {void}
   */
  drawActiveElBounds(activeEl) {
    if (!activeEl.ctx) {
      // active element is not ready to be rendered
      return;
    }

    const savedProperties = {
      width: activeEl.width,
      height: activeEl.height,
      position: new Vector(activeEl.position.x, activeEl.position.y),
    };
    const scale = this.engineParams.scale;

    this.virtualCanvas
      .save()
      .translate(
        (-this.boundingBox.width / 2) * (scale - 1),
        (-this.boundingBox.height / 2) * (scale - 1)
      );

    activeEl.preRender();
    activeEl.beforeRenderBounds();

    if (!activeEl.customBounds()) {
      activeEl.renderBounds();
    }

    if (!activeEl.editMode) {
      activeEl.drawControls();
    }

    activeEl.afterRenderBounds();

    this.virtualCanvas.restore();
    Object.assign(activeEl, savedProperties);
  }

  drawStaticLayerImages() {
    const activeLayer = this.engine.activeLayer;
    const bounds = this.boundingBox;
    const canvasScale = this.engineParams.scale;

    if (
      this.engineParams.background.imageEnabled &&
      activeLayer &&
      activeLayer?.images?.background
    ) {
      this.drawLayerBackground(activeLayer, bounds, canvasScale);
    }

    if (
      this.engineParams.foreground.imageEnabled &&
      activeLayer &&
      activeLayer?.images?.foreground
    ) {
      this.drawLayerForeground(activeLayer, bounds, canvasScale);
    }
  }

  drawGhostLayers() {
    const activeLayer = this.engine.activeLayer;
    if (!activeLayer.ghostLayers) {
      return;
    }

    Object.keys(activeLayer.ghostLayers).forEach(key => {
      const ghostLayer = activeLayer.ghostLayers[key];
      const cachedLayer = this.getCachedGhostLayer(activeLayer.tag, key);
      if (!cachedLayer || cachedLayer.needsRefreshing) {
        if (
          !this.engine.getObjects(key).length ||
          this.engineParams.exportMode
        ) {
          return;
        }

        requestAnimationFrame(() => {
          if (this.engineParams.exportMode) {
            return;
          }
          const originalLayer = this.engine.activeLayer;
          this.engine.setActiveLayer(key);
          const img = new Image();
          img.onload = () => {
            if (this.boundingBox.cutout) {
              const cnv = new SierraCanvas();
              cnv.resizeCanvas(
                this.virtualCanvas.width,
                this.virtualCanvas.height
              );
              cnv
                .save()
                .when(ghostLayer.rotation, cnv => {
                  cnv.rotateAroundPoint(
                    ghostLayer.rotation * (Math.PI / 180),
                    ghostLayer.position.x,
                    ghostLayer.position.y
                  );
                })
                .drawImage(
                  img,
                  ghostLayer.position.x,
                  ghostLayer.position.y,
                  ghostLayer.width,
                  ghostLayer.height
                )
                .restore()
                .setCompositeOperation('destination-in')
                .drawImage(
                  this.boundingBox.cutout,
                  0,
                  0,
                  this.virtualCanvas.width,
                  this.boundingBox.cutout.height *
                    (this.virtualCanvas.width / this.boundingBox.cutout.width)
                );
              this.cacheGhostLayer(activeLayer.tag, key, cnv.canvas);
            } else {
              const cnv = new SierraCanvas();
              cnv.resizeCanvas(
                this.virtualCanvas.width,
                this.virtualCanvas.height
              );
              cnv
                .save()
                .when(ghostLayer.rotation, cnv => {
                  cnv.rotateAroundPoint(
                    ghostLayer.rotation * (Math.PI / 180),
                    ghostLayer.position.x,
                    ghostLayer.position.y
                  );
                })
                .drawImage(
                  img,
                  ghostLayer.position.x,
                  ghostLayer.position.y,
                  ghostLayer.width,
                  ghostLayer.height
                )
                .restore();
              this.cacheGhostLayer(activeLayer.tag, key, cnv);
            }
          };
          img.src = this.export('image/png', 0.92, 1, 1);
          this.engine.setActiveLayer(originalLayer.tag);
        });
      }

      if (cachedLayer) {
        const canvasScale = this.engineParams.scale;
        this.virtualCanvas.drawImage(
          cachedLayer.image,
          -(this.virtualCanvas.width * (canvasScale - 1)) / 2,
          -(this.virtualCanvas.height * (canvasScale - 1)) / 2,
          this.virtualCanvas.width * canvasScale,
          this.virtualCanvas.height * canvasScale
        );
      }
    });
  }

  cacheGhostLayer(baseLayer, ghostLayer, image) {
    if (!this.ghostLayerCache[baseLayer]) {
      this.ghostLayerCache[baseLayer] = {};
    }

    this.ghostLayerCache[baseLayer][ghostLayer] = {
      needsRefreshing: false,
      image,
    };
  }

  getCachedGhostLayer(baseLayer, ghostLayer) {
    return this.ghostLayerCache[baseLayer]?.[ghostLayer] ?? undefined;
  }

  onChangeLayer(fromLayer, toLayer) {
    Object.values(this.ghostLayerCache).forEach(layer => {
      if (layer[toLayer]) {
        layer[toLayer].needsRefreshing = true;
      }
    });
  }

  /**
   * This renders an image behind the canvas content
   *
   * @param {Object} activeLayerObject the current layer object
   * @param {OmniBounds} bounds the bounds of the current layer
   *
   * @return {void}
   */
  drawLayerBackground(activeLayerObject, bounds) {
    if (!this.canvasElement.parentElement) {
      // Canvas has not yet been fully initialised
      return;
    }

    const layerBackground = activeLayerObject.images.background;

    this.virtualCanvas
      .save()
      .setCompositeOperation('destination-over')
      .scaleFromPosition(
        this.engineParams.scale,
        this.engineParams.scale,
        bounds.position.x + bounds.width / 2,
        bounds.position.y + bounds.height / 2
      )
      .drawImage(
        layerBackground.image,
        layerBackground.x,
        layerBackground.y,
        layerBackground.w,
        layerBackground.h
      )
      .restore();
  }

  drawLayerForeground(activeLayerObject) {
    const bounds = this.boundingBox;

    const layerForeground = activeLayerObject.images.foreground;
    const scale = this.engineParams.scale;

    this.virtualCanvas
      .save()
      .setGlobalAlpha(this.engineParams.foreground.opacity)
      .scaleFromPosition(
        scale,
        scale,
        bounds.position.x + bounds.width / 2,
        bounds.position.y + bounds.height / 2
      )
      .drawImage(
        layerForeground.image,
        0,
        0,
        this.virtualCanvas.width,
        (this.virtualCanvas.width * layerForeground.image.height) /
          layerForeground.image.width
      )
      .restore();
  }

  drawBackgroundFill() {
    const fill = this.engineParams.background.fill;
    const bounds = this.bounds;

    this.virtualCanvas
      .save()
      .setCompositeOperation('destination-over')
      .scaleFromPosition(
        this.engineParams.scale,
        this.engineParams.scale,
        bounds.position.x + bounds.width / 2,
        bounds.position.y + bounds.height / 2
      );

    if (typeof fill === 'string') {
      this.virtualCanvas
        .setFillStyle(fill)
        .fillRect(
          bounds.position.x,
          bounds.position.y,
          bounds.width,
          bounds.height
        );
    }

    if (fill instanceof Image || fill instanceof HTMLCanvasElement) {
      this.virtualCanvas.drawImageToCover(
        fill,
        bounds.position.x,
        bounds.position.y,
        bounds.width,
        bounds.height
      );
    }

    if (fill instanceof ColorGradient) {
      this.virtualCanvas.angularGradientFill(
        fill.toObject(),
        fill.direction,
        bounds.position.x,
        bounds.position.y,
        bounds.width,
        bounds.height
      );
    }

    if (this.engineParams.background.shadow) {
      this.virtualCanvas
        .setShadow(2, 2, 6, 'rgba(0,0,0,0.2)')
        .fillRect(
          bounds.position.x,
          bounds.position.y,
          bounds.width,
          bounds.height
        );
    }

    this.virtualCanvas.restore();
  }

  /**
   * Counts how many unique colours there are in the canvas
   *
   * @param {Int} accuracy This determines how close two colours need to be in order to
   * be considered the same colour. Higher values will mean more colours are treated as
   * the same, while lower values may mean that two colours that appear the same to the
   * human eye are considered as two distinct colours. For best results, use values
   * between 16 and 64, and stick to powers of two (1, 2, 4 ,8, 16, 32, 64, 128, 256)
   *
   * @return {Int}
   */
  countUniqueColours(accuracy = 32) {
    return this.getUniqueColours(accuracy).length;
  }

  /**
   * Finds distinct colours in the canvas and returns them as an array of hexes
   *
   * @param {Int} accuracy This determines how close two colours need to be in order to
   * be considered the same colour. Higher values will mean more colours are treated as
   * the same, while lower values may mean that two colours that appear the same to the
   * human eye are considered as two distinct colours. For best results, use values
   * between 16 and 64, and stick to powers of two (1, 2, 4 ,8, 16, 32, 64, 128, 256)
   *
   * @return {String[]}
   */
  getUniqueColours(accuracy = 32) {
    this.engine.activeEl = undefined;
    this.render({
      includeBounds: false,
      includeStaticLayerImages: false,
    });

    const bounds = this.boundingBox;

    let rgb;
    const allCols = {};
    let pixelCount = 0;

    this.virtualCanvas.forEachPixel(
      ({r, g, b, a}) => {
        // disregard any pixels that are < 50% opacity
        if (a >= 128) {
          r = Math.round(r / accuracy) * accuracy;
          g = Math.round(g / accuracy) * accuracy;
          b = Math.round(b / accuracy) * accuracy;

          pixelCount += 1;

          rgb = `rgb(${r},${g},${b})`;

          if (!allCols[rgb]) {
            allCols[rgb] = 1;
          } else {
            allCols[rgb]++;
          }
        }
      },
      bounds.position.x,
      bounds.position.y,
      bounds.width,
      bounds.height
    );

    const uniqueColours = [];

    Object.keys(allCols).forEach(i => {
      // colour must occupy at least 1% of total coloured pixels
      if (allCols[i] > pixelCount / 100) {
        const [r, g, b] = i.match(/[0-9]+/g);
        uniqueColours.push(
          rgbToHex({
            r: parseInt(r, 10),
            g: parseInt(g, 10),
            b: parseInt(b, 10),
          })
        );
      }
    });

    this.render();

    return uniqueColours;
  }

  /**
   * Used to apply filters to all objects
   * @see OmniObject applyFilters()
   *
   * @return {void}
   */
  applyFilters() {
    if (this.engineParams.newFilters === true) {
      this.engine.getObjects().forEach(object => {
        object.applyFilters();
      });
    }

    this.engineParams.newFilters = false;
  }

  /**
   * Add a filter to all objects
   *
   * @see OmnisFilters
   *
   * @param {String} filterName The name of the filter to apply
   * @param {Any} adjustmentVal This defines the strength and other values
   * of the filter. The exact value will vary depending on the filter being
   * applied.
   *
   * @return {void}
   */
  addFilter(filterName, adjustmentVal) {
    this.engine.getObjects().forEach(object => {
      object.addFilter(filterName, adjustmentVal);
    });

    this.engineParams.newFilters = true;
  }

  /**
   * Remove all filters from all objects
   *
   * @return {void}
   */
  clearFilters() {
    this.engineParams.filters = {};
    this.engine.getObjects().forEach(object => {
      object.clearFilters();
    });
  }

  createScaledCopy(scale, renderOptions = {}, inBounds = true) {
    this.suppressOutput();
    const startingCanvasScale = this.engineParams.scale;

    // set scale to 1 so that we can render the full canvas
    this.engineParams.scale = 1;
    this.render({async: false, ...renderOptions});

    let output;

    if (inBounds) {
      const bounds = this.boundingBox;
      output = this.virtualCanvas.createScaledCopy(
        scale,
        bounds.position.x,
        bounds.position.y,
        bounds.width,
        bounds.height
      );
    } else {
      output = this.virtualCanvas.createScaledCopy(scale);
    }

    // restore scale to its original value
    this.engineParams.scale = startingCanvasScale;
    this.queueRender();

    this.unsuppressOutput();
    return output;
  }

  updateDimensions(width, height) {
    this.outputCanvas.resizeCanvas(Math.round(width), Math.round(height));
    this.virtualCanvas.resizeToImage(this.outputCanvas.canvas);
  }

  debug() {
    this.appendCanvases();
  }

  appendCanvases() {
    document.body.append(this.virtualCanvas.canvas);
    document.body.append(this.cacheCanvas.canvas);
  }

  destroy() {
    this.outputCanvas.destroy();
    this.virtualCanvas.destroy();
    this.cacheCanvas.destroy();

    this.outputCanvas = null;
    this.virtualCanvas = null;
    this.cacheCanvas = null;
    this.canvasElement = null;

    this.engine = null;
  }

  renderCallbacks = [];

  defineRenderCallback(callback) {
    this.renderCallbacks.push(callback);
  }

  runRenderCallbacks() {
    const callbacks = this.renderCallbacks;

    this.renderCallbacks = [];

    callbacks.forEach(callback => {
      callback();
    });
  }
}
